import {
  EditorContext,
  filesEqual,
  useCodebaseStore,
} from "@/store/zustand/codebaseStore";
import useLayoutStore, {
  LayoutMode,
  LeftTab,
} from "@/store/zustand/layoutStore";
import { Message, useSupabaseStore } from "@/store/zustand/supabaseStore";
import { useCallback, useMemo } from "react";
import { toast } from "sonner";
import { useEditPrd } from "../PRD/hooks/useEditPrd";
import { MessageSegment } from "./utils/messages";
import { applyPatch } from "diff";
import { EngSpecFileDiff } from "../EngSpec/EngSpec";
import axios from "axios";

export interface AiCodeEditBlock {
  filename: string;
  edit: {
    type: string;
    startLine: number;
    endLine: number;
    newContent: string[];
    oldContents?: string;
  };
}

export const applyReplacemenetBlock = (
  replacement: {
    startLine: number;
    endLine: number;
    newContent: string[];
  },
  fileContents: string,
): string => {
  if (!replacement.newContent) return fileContents;
  if (fileContents === undefined || fileContents === null) return fileContents;
  if (!replacement.startLine && replacement.startLine !== 0)
    return fileContents;

  const fileLinesArray = fileContents.split("\n");
  fileLinesArray.splice(
    replacement.startLine - 1,
    replacement.endLine
      ? replacement.endLine - replacement.startLine
      : fileLinesArray.length - replacement.startLine + 1,
    ...replacement.newContent,
  );
  return fileLinesArray.join("\n");
};

export const applyGitDiffToContents = (
  diff: string,
  fileContents: string,
): { newContents: string; succeeded: boolean } => {
  if (fileContents === undefined || fileContents === null)
    return { newContents: fileContents, succeeded: false };
  if (!diff) return { newContents: fileContents, succeeded: false };

  const diffLines = diff.split("\n");
  const diffBlocks = [];
  let currentBlocks = "";
  diffLines.forEach((line) => {
    if (line.startsWith("@@")) {
      if (currentBlocks) {
        // @ts-expect-error TS2345
        diffBlocks.push(currentBlocks);
        currentBlocks = "";
      }
    }

    currentBlocks += line + "\n";
  });
  // @ts-expect-error TS2345
  currentBlocks && diffBlocks.push(currentBlocks);

  // Apply the blocks in reverse order to avoid shifting
  let newFile = fileContents;
  let success = false;
  try {
    diffBlocks.reverse();
    diffBlocks.forEach((block) => {
      const afterPatch = applyPatch(newFile, block);

      // Can also fail this way
      if (afterPatch === false) {
        throw new Error("Patch failed: " + block);
      }

      newFile = afterPatch;
      success = true;
    });
  } catch (e) {
    console.error(e);
    console.error({ diff, fileContents });
    success = false;
    newFile = fileContents;
  }

  return { newContents: newFile, succeeded: success };
};

export const VALID_COMMANDS = new Set<string>([
  "create",
  "str_replace",
  "insert",
  "overwrite",
]);

export const applyTextEditorToContents = async (
  textEditorCommand: {
    command?: "create" | "str_replace" | "insert" | "overwrite";
    path?: string;
    old_str?: string;
    new_str?: string;
    insert_line?: number;
    file_text?: string;
    replace_all?: boolean;
  },
  prevFileContents: string,
  canvasId: string,
  supabaseJWT: string,
  relaceFallbackSettings?: {
    useRelaceAsFallback?: boolean;
    originalUserPrompt?: string;
    aiChatTraceId?: string;
  },
): Promise<{ newContents: string; succeeded: boolean; error?: string }> => {
  try {
    if (
      textEditorCommand.command === "create" ||
      textEditorCommand.command === "overwrite"
    ) {
      return {
        newContents: textEditorCommand.file_text || "",
        succeeded: true,
      };
    } else if (textEditorCommand.command === "str_replace") {
      if (!prevFileContents) {
        return { newContents: prevFileContents || "", succeeded: true };
      }

      if (!textEditorCommand.old_str) {
        return {
          newContents: prevFileContents || "",
          succeeded: false,
          error: "No old string to replace",
        };
      }

      if (textEditorCommand.new_str === textEditorCommand.old_str) {
        // If the new string is the same as the old string, we can assume success without making any changes
        return {
          newContents: prevFileContents || "",
          succeeded: true,
        };
      }

      const searchValue = textEditorCommand.old_str;

      // Check if search value shows up multiple times in the file
      const searchValueCount =
        (prevFileContents || "").split(searchValue).length - 1;

      let newContents = "";
      let succeeded = false;

      if (searchValueCount > 1 && textEditorCommand.replace_all) {
        // Multiple occurrences - replace all with global regex
        newContents =
          prevFileContents?.replace(
            new RegExp(searchValue, "g"),
            textEditorCommand.new_str || "",
          ) || "";
      } else if (searchValueCount >= 1) {
        // Single occurrence OR multiple occurrences with replace_all=false - replace first occurrence only
        // AI is instructed to generate replace_all=false when the change is contextual and should only affect one location
        newContents =
          prevFileContents?.replace(
            searchValue,
            textEditorCommand.new_str || "",
          ) || "";
      }

      // Success is determined by whether content actually changed
      succeeded = newContents !== prevFileContents;

      // Search value not found in file, use relace as fallback
      if (
        searchValueCount === 0 &&
        relaceFallbackSettings?.useRelaceAsFallback
      ) {
        const url = `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/ai/relace/code/apply`;

        const initialCode = prevFileContents || "";
        const editSnippet = `// Replace this \n\n ${
          textEditorCommand.old_str
        } \n\n // with \n\n ${textEditorCommand.new_str || ""}`;

        try {
          const response = await axios.post(
            url,
            {
              initialCode,
              editSnippet,
              filename: textEditorCommand.path,
              userPrompt: relaceFallbackSettings?.originalUserPrompt,
              traceIdOverride: relaceFallbackSettings?.aiChatTraceId,
            },
            {
              headers: {
                Authorization: `Bearer ${supabaseJWT}`,
              },
            },
          );

          const mergedCode = response.data.mergedCode;

          newContents = mergedCode;
          succeeded = true;
        } catch (e) {
          console.error(e);
          console.error(
            "Failed to apply text editor command",
            textEditorCommand,
          );
          succeeded = false;
        }
      }

      const error = succeeded
        ? undefined
        : newContents === prevFileContents
          ? "Did not change contents"
          : searchValueCount > 1
            ? "Search value shows up multiple times"
            : searchValueCount === 0
              ? "Search value does not show up in the file"
              : undefined;

      return {
        newContents: newContents || "",
        succeeded: succeeded,
        error: error,
      };
    } else if (textEditorCommand.command === "insert") {
      // Insert the new_str at the insert_line
      const lines = prevFileContents?.split("\n");

      // @ts-expect-error TS18048
      if (textEditorCommand.insert_line > lines.length) {
        return {
          newContents: prevFileContents || "",
          succeeded: false,
          error: "Insert line is out of bounds",
        };
      }

      // @ts-expect-error TS18048
      lines.splice(textEditorCommand.insert_line, 0, textEditorCommand.new_str);
      return {
        newContents: lines.join("\n"),
        succeeded: true,
      };
    }

    return { newContents: prevFileContents || "", succeeded: true };
  } catch (e) {
    console.error(e);
    console.error("Failed to apply text editor command", textEditorCommand);
    return {
      newContents: prevFileContents || "",
      succeeded: false,
      error: "Error thrown while applying text editor command",
    };
  }
};

export const useApplyChatBlock = () => {
  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);

  const fileToContentsFromApi = useCodebaseStore(
    (state) => state.fileToContents,
  );

  const editorState = useCodebaseStore((state) => state.editor);

  const onEditPrd = useEditPrd();

  const _canApplyCodeChange = useCallback(
    (message: Message, item: any) => {
      // Check if the file contents match
      const codeChange = item.aiCodeChange as AiCodeEditBlock;

      const fileContentsToCheck = fileToContentsFromApi[codeChange?.filename];

      const currentContents = fileContentsToCheck
        ?.split("\n")
        .slice(
          codeChange.edit.startLine !== undefined
            ? codeChange.edit.startLine - 1
            : 0,
          codeChange.edit.endLine || undefined,
        )
        .join("\n");

      const oldContents = codeChange.edit.oldContents;

      return currentContents === oldContents;
    },
    [fileToContentsFromApi],
  );

  const canApplyChatBlock = useCallback(
    (message: Message, item: any) => {
      try {
        if (item.type === "aiCodeChange") {
          return _canApplyCodeChange(message, item);
        }

        return false;
      } catch (e) {
        return false;
      }
    },
    [_canApplyCodeChange],
  );

  const applyChatBlock = useCallback(
    async (
      message: Message,
      item: MessageSegment,
      index: number,
      extraContext?: { [key: string]: any },
    ) => {
      if (!supabaseClient) {
        return;
      }

      if (item.type === "aiCodeChange") {
        if (!_canApplyCodeChange(message, item)) {
          toast.error(`Code has changed since the AI suggested this change.`);
          return;
        }

        const aiCodeChange = item.aiCodeChange as AiCodeEditBlock;

        const fileContents = fileToContentsFromApi[aiCodeChange.filename];
        const newFileContents = applyReplacemenetBlock(
          aiCodeChange.edit,
          fileContents,
        );

        // Open the code panel if it's not open
        useLayoutStore.getState().setNewLayout(LeftTab.AIChat, LayoutMode.Code);

        // Check if file is opened (ignoring the suggested diff)
        const existingFile = editorState[EditorContext.Code]?.editorFiles?.find(
          (file) =>
            filesEqual(
              {
                ...file,
                // @ts-expect-error TS2322
                suggestedDiff: null,
              },
              {
                filename: aiCodeChange.filename,
                context: EditorContext.Code,
              },
            ),
        );

        if (existingFile) {
          useCodebaseStore.getState().updateFile(existingFile, {
            suggestedDiff: newFileContents,
            hardOpened: true,
          });
        } else {
          useCodebaseStore.getState().openFile({
            file: {
              filename: aiCodeChange.filename,
              suggestedDiff: newFileContents,
              context: EditorContext.Code,
              hardOpened: true,
            },
            jumpToTab: true,
          });
        }
      } else if (item.type === "prd-json") {
        const newPrd = item.parsedJson?.prd || "";
        onEditPrd("prd", newPrd);
      }

      const updatedMessage: any = {
        ...message,
        message_context: {
          ...message.message_context,
          applied_segment_indices: [
            ...(message.message_context?.applied_segment_indices || []),
            index,
          ],
          ...(extraContext || {}),
        },
      };

      useSupabaseStore.getState().onDbSnapshot("aiChatMessages", {
        newItems: [updatedMessage],
      });

      await supabaseClient
        .from("ai_chat_messages")
        .update(updatedMessage)
        .eq("id", message.id);
    },
    [
      supabaseClient,
      _canApplyCodeChange,
      fileToContentsFromApi,
      editorState,
      onEditPrd,
    ],
  );

  const updoApplyChatBlock = useCallback(
    async (message: Message, item: MessageSegment, index: number) => {
      // Currently, can only undo prd-json
      if (!supabaseClient || item.type !== "prd-json") {
        return;
      }

      // @ts-expect-error TS18048
      const newPrd = message.message_context.previousPrd || "";
      onEditPrd("prd", newPrd);

      const updatedMessage: any = {
        ...message,
        message_context: {
          ...message.message_context,
          applied_segment_indices: [
            ...(message.message_context?.applied_segment_indices || []).filter(
              (i) => i !== index,
            ),
          ],
        },
      };

      useSupabaseStore.getState().onDbSnapshot("aiChatMessages", {
        newItems: [updatedMessage],
      });

      await supabaseClient
        .from("ai_chat_messages")
        .update(updatedMessage)
        .eq("id", message.id);
    },
    [supabaseClient, onEditPrd],
  );

  return {
    applyChatBlock,
    canApplyChatBlock,
    updoApplyChatBlock,
  };
};

export const useCombinedFileChanges = (
  fileChanges: EngSpecFileDiff[],
  streaming?: boolean,
) => {
  return useMemo(() => {
    const changes: EngSpecFileDiff[] = [];
    const pathToExistingFile = {};

    fileChanges?.forEach((change) => {
      if (change.path && pathToExistingFile[change.path]) {
        // Set to the last id
        pathToExistingFile[change.path].id = change.id;
        return;
      }

      // HACK Jan 20, 2025 -> for old messages the path is under filePath, not path, reassign
      // Remove after a few days
      const newFile = {
        id: change.id,
        summary: change.summary,
        termCommand: change.termCommand,
        storyboardName: change.storyboardName,
        runSqlMigration: change.runSqlMigration,
        deployEdgeFunction: change.deployEdgeFunction,
        path: change.filePath || change.path,
        runConvexSync: change.runConvexSync,
        executedActivityId: change.executedActivityId,
        hasChange: Boolean(
          change?.command || change?.change?.newFullFileContent,
        ),
      };

      const isEmptyObject =
        Object.keys(newFile).filter((key) => {
          return key !== "id" && !newFile[key];
        }).length ===
        Object.keys(newFile).length - 1;

      // Don't show empty objects
      if (isEmptyObject) {
        return;
      }

      // When streaming this means the path might not be fully loaded yet, so skip this file to avoid jumping
      if (
        streaming &&
        change?.command &&
        !(change?.old_str || change?.new_str || change?.file_text)
      ) {
        return;
      }

      changes.push(newFile);
      if (newFile.path) {
        pathToExistingFile[newFile.path] = newFile;
      }
    });

    return changes;
  }, [fileChanges, streaming]);
};
